import type { Solicitacao, Movimentacao, StatusSolicitacao, TipoSolicitacao } from '../../../../models';
import { calcularAlertaMovimentacao, TIPOS_SOLICITACAO_SEM_MOVIMENTACAO } from '../../../../models';
import { pendenciaSolicitacao } from '../../../SolicitacaoDetailsPage/pendenciaMovimentacao';
import { STATUS_SOLICITACAO_LABELS } from '../../solicitacoesListPage.definitions';
import { TIPO_SOLICITACAO_LABELS } from '../../../NovaSolicitacaoPage/novaSolicitacaoPage.definitions';

const ORDEM_STATUS: StatusSolicitacao[] = ['aberta', 'em_andamento', 'concluida', 'cancelada', 'excluida'];

export interface PontoFunilStatus {
  status: StatusSolicitacao;
  label: string;
  quantidade: number;
}

export function calcularFunilStatus(itens: Solicitacao[]): PontoFunilStatus[] {
  const contagem = new Map<StatusSolicitacao, number>();
  for (const solicitacao of itens) {
    contagem.set(solicitacao.status, (contagem.get(solicitacao.status) ?? 0) + 1);
  }
  return ORDEM_STATUS.map((status) => ({
    status,
    label: STATUS_SOLICITACAO_LABELS[status],
    quantidade: contagem.get(status) ?? 0,
  }));
}

export interface PontoVolumePorTipo {
  tipo: TipoSolicitacao;
  label: string;
  quantidade: number;
}

/** Só os tipos com pelo menos 1 Solicitação aparecem — evita poluir o gráfico com barras
 * zeradas pros tipos menos comuns. Ordenado do mais pro menos frequente. */
export function calcularVolumePorTipo(itens: Solicitacao[]): PontoVolumePorTipo[] {
  const contagem = new Map<TipoSolicitacao, number>();
  for (const solicitacao of itens) {
    contagem.set(solicitacao.tipoSolicitacao, (contagem.get(solicitacao.tipoSolicitacao) ?? 0) + 1);
  }
  return Array.from(contagem.entries())
    .map(([tipo, quantidade]) => ({ tipo, label: TIPO_SOLICITACAO_LABELS[tipo], quantidade }))
    .sort((a, b) => b.quantidade - a.quantidade);
}

function paraMillis(timestamp: Solicitacao['createdAt'] | null | undefined): number | null {
  if (!timestamp) return null;
  return typeof (timestamp as { toMillis?: () => number }).toMillis === 'function'
    ? (timestamp as { toMillis: () => number }).toMillis()
    : new Date(timestamp as unknown as string).getTime();
}

export interface PontoTempoMedioConclusao {
  tipo: TipoSolicitacao;
  label: string;
  mediaDias: number;
  quantidade: number;
}

/** Só considera Solicitações Concluídas com as duas datas presentes — as demais não têm
 * "tempo total" ainda definido. Tipos sem nenhuma Concluída ficam de fora (média de 0 item
 * seria enganosa, não "rápida"). */
export function calcularTempoMedioConclusao(itens: Solicitacao[]): PontoTempoMedioConclusao[] {
  const diasPorTipo = new Map<TipoSolicitacao, number[]>();

  for (const solicitacao of itens) {
    if (solicitacao.status !== 'concluida') continue;
    const abertura = paraMillis(solicitacao.dataAbertura);
    const conclusao = paraMillis(solicitacao.dataConclusao);
    if (abertura == null || conclusao == null) continue;

    const dias = (conclusao - abertura) / (1000 * 60 * 60 * 24);
    const lista = diasPorTipo.get(solicitacao.tipoSolicitacao) ?? [];
    lista.push(dias);
    diasPorTipo.set(solicitacao.tipoSolicitacao, lista);
  }

  return Array.from(diasPorTipo.entries())
    .map(([tipo, lista]) => ({
      tipo,
      label: TIPO_SOLICITACAO_LABELS[tipo],
      mediaDias: Math.round((lista.reduce((soma, dias) => soma + dias, 0) / lista.length) * 10) / 10,
      quantidade: lista.length,
    }))
    .sort((a, b) => b.mediaDias - a.mediaDias);
}

/** Mesmo critério já usado em SolicitacoesListPage/SolicitacaoDetailsContent — a
 * Movimentação ainda não finalizada (se houver), senão a mais recente. */
function movimentacaoRelevante(movimentacoes: Movimentacao[]): Movimentacao | null {
  if (movimentacoes.length === 0) return null;
  const ativa = movimentacoes.find(
    (m) => m.statusMovimentacao !== 'entregue' && m.statusMovimentacao !== 'cancelada'
  );
  return ativa ?? movimentacoes[movimentacoes.length - 1];
}

export interface SolicitacaoAtrasada {
  id: string;
  colaboradorNome: string;
  tipoLabel: string;
  alertaTexto: string;
}

/** Formatação/Acessos nunca têm Movimentação, então nunca teriam o alerta de urgência
 * baseado em `dataPrevista` (ver abaixo) — sem esse limite, ficavam invisíveis em
 * "Urgentes Agora" pra sempre, não importa há quanto tempo estivessem abertas. 1 dia
 * porque, na prática, esses tipos costumam ser resolvidos rápido — passou disso, já vale
 * cobrar. */
const LIMITE_DIAS_URGENTE_SEM_MOVIMENTACAO = 1;

/** "Urgente" é o próprio nível de urgência que a Solicitação já mostra em qualquer lista
 * (mesmo cálculo do badge de alerta) — aqui só filtra quem está nesse nível agora, pra dar
 * uma visão consolidada sem precisar abrir cada Solicitação. Pra tipos sem Movimentação
 * (Formatação, Acessos), não existe esse alerta — usa tempo desde a abertura como proxy. */
export function listarAtrasadas(itens: Solicitacao[], movimentacoesPorSolicitacao: Map<string, Movimentacao[]>): SolicitacaoAtrasada[] {
  const resultado: SolicitacaoAtrasada[] = [];
  for (const solicitacao of itens) {
    if (solicitacao.status !== 'aberta' && solicitacao.status !== 'em_andamento') continue;

    if (TIPOS_SOLICITACAO_SEM_MOVIMENTACAO.includes(solicitacao.tipoSolicitacao)) {
      const abertura = paraMillis(solicitacao.dataAbertura);
      if (abertura == null) continue;
      const diasAberta = (Date.now() - abertura) / (1000 * 60 * 60 * 24);
      if (diasAberta < LIMITE_DIAS_URGENTE_SEM_MOVIMENTACAO) continue;
      resultado.push({
        id: solicitacao.id,
        colaboradorNome: solicitacao.colaboradorNome,
        tipoLabel: TIPO_SOLICITACAO_LABELS[solicitacao.tipoSolicitacao],
        alertaTexto: `🚨 Aberta há ${Math.floor(diasAberta)}+ dia(s)`,
      });
      continue;
    }

    const movimentacao = movimentacaoRelevante(movimentacoesPorSolicitacao.get(solicitacao.id) ?? []);
    if (!movimentacao) continue;
    const alerta = calcularAlertaMovimentacao(movimentacao);
    if (alerta.nivel !== 'urgente') continue;
    resultado.push({
      id: solicitacao.id,
      colaboradorNome: solicitacao.colaboradorNome,
      tipoLabel: TIPO_SOLICITACAO_LABELS[solicitacao.tipoSolicitacao],
      alertaTexto: `${alerta.emoji} ${alerta.texto}`,
    });
  }
  return resultado;
}

export interface PeriodoFiltro {
  /** `null` = sem limite inferior/superior (ver `filtrarPorPeriodo`). */
  de: Date | null;
  ate: Date | null;
}

/** Filtra pela `dataAbertura` — todas as métricas do Dashboard passam a refletir só as
 * Solicitações abertas dentro do período escolhido (mês individual, período livre, hoje
 * etc.), em vez de sempre a fila inteira. `{ de: null, ate: null }` = sem filtro. */
export function filtrarPorPeriodo(itens: Solicitacao[], periodo: PeriodoFiltro): Solicitacao[] {
  if (periodo.de == null && periodo.ate == null) return itens;
  return itens.filter((solicitacao) => {
    const abertura = paraMillis(solicitacao.dataAbertura);
    if (abertura == null) return false;
    if (periodo.de != null && abertura < periodo.de.getTime()) return false;
    if (periodo.ate != null && abertura > periodo.ate.getTime()) return false;
    return true;
  });
}

export interface PontoPendenciaComum {
  label: string;
  quantidade: number;
}

/** Só entre as Solicitações ainda ativas (Novo/Em Andamento) — Concluída não tem pendência
 * por definição (é exigida pra concluir), e Cancelada/Excluída não são mais trabalho em
 * aberto, mostrar pendência delas só teria valor histórico, não "o que está travando agora". */
export function calcularPendenciasComuns(
  itens: Solicitacao[],
  movimentacoesPorSolicitacao: Map<string, Movimentacao[]>,
  limite = 6
): PontoPendenciaComum[] {
  const contagem = new Map<string, number>();
  for (const solicitacao of itens) {
    if (solicitacao.status !== 'aberta' && solicitacao.status !== 'em_andamento') continue;
    const movimentacoes = movimentacoesPorSolicitacao.get(solicitacao.id) ?? [];
    const pendencia = pendenciaSolicitacao(solicitacao, movimentacoes);
    if (!pendencia) continue;
    const chave = `${pendencia.emoji} ${pendencia.label}`;
    contagem.set(chave, (contagem.get(chave) ?? 0) + 1);
  }
  return Array.from(contagem.entries())
    .map(([label, quantidade]) => ({ label, quantidade }))
    .sort((a, b) => b.quantidade - a.quantidade)
    .slice(0, limite);
}
